import React, { useEffect, useState, useMemo } from "react";
import { useApi } from "@/lib/auth";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { money, stageLabel, STAGE_COLORS, STAGES } from "@/lib/format";
import { Upload, Search, X } from "lucide-react";

export default function Leads() {
  const { get } = useApi();
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const q = params.get("q") || "";
  const stage = params.get("stage") || "";
  const qual = params.get("qualification") || "";

  const [search, setSearch] = useState(q);

  const load = async () => {
    setLoading(true);
    const qs = new URLSearchParams();
    if (q) qs.set("q", q);
    if (stage) qs.set("stage", stage);
    if (qual) qs.set("qualification", qual);
    try {
      const r = await get(`/contacts?${qs.toString()}`);
      setItems(r.data.items || []);
      setTotal(r.data.total);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [q, stage, qual]); // eslint-disable-line

  const updateParam = (key, val) => {
    const np = new URLSearchParams(params);
    if (val) np.set(key, val); else np.delete(key);
    setParams(np);
  };

  const clearAll = () => setParams(new URLSearchParams());

  return (
    <div className="space-y-5">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-slate-500">CRM</p>
          <h1 className="font-display text-3xl font-black tracking-tighter text-slate-900 mt-1">
            Leads & Contacts
          </h1>
          <p className="text-sm text-slate-600 mt-1">{total.toLocaleString()} contacts in the database.</p>
        </div>
        <div className="flex gap-2">
          <Button
            data-testid="import-leads-btn"
            onClick={() => nav("/leads/import")}
            className="bg-slate-900 hover:bg-slate-800 text-white"
          >
            <Upload className="h-4 w-4 mr-1.5" /> Import leads
          </Button>
        </div>
      </div>

      <Card className="p-3 border-slate-200">
        <form
          className="flex flex-wrap gap-2 items-center"
          onSubmit={(e) => { e.preventDefault(); updateParam("q", search); }}
        >
          <div className="relative flex-1 min-w-[240px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              data-testid="leads-search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Name, case #, property, county…"
              className="pl-9 h-9"
            />
          </div>

          <Select value={stage || "all"} onValueChange={(v) => updateParam("stage", v === "all" ? "" : v)}>
            <SelectTrigger className="w-44 h-9" data-testid="leads-stage-filter">
              <SelectValue placeholder="All stages" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All stages</SelectItem>
              {STAGES.map(s => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>

          <Select value={qual || "all"} onValueChange={(v) => updateParam("qualification", v === "all" ? "" : v)}>
            <SelectTrigger className="w-44 h-9" data-testid="leads-qualification-filter">
              <SelectValue placeholder="All qualifications" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All qualifications</SelectItem>
              <SelectItem value="qualified">Qualified</SelectItem>
              <SelectItem value="research">Needs Research</SelectItem>
              <SelectItem value="disqualified">Disqualified</SelectItem>
            </SelectContent>
          </Select>

          <Button type="submit" variant="secondary" data-testid="leads-apply-search-btn">
            Search
          </Button>
          {(q || stage || qual) && (
            <Button type="button" variant="ghost" onClick={() => { setSearch(""); clearAll(); }} data-testid="leads-clear-filters-btn">
              <X className="h-4 w-4 mr-1" /> Clear
            </Button>
          )}
        </form>
      </Card>

      <Card className="overflow-hidden border-slate-200 p-0">
        <div className="overflow-x-auto">
          <table className="cw-table w-full">
            <thead>
              <tr>
                <th>Claimant</th>
                <th>Property</th>
                <th>County · State</th>
                <th className="text-right">Surplus</th>
                <th>Case #</th>
                <th>Stage</th>
                <th className="text-right">Score</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={7} className="text-center py-8 text-slate-500">Loading…</td></tr>
              )}
              {!loading && items.length === 0 && (
                <tr>
                  <td colSpan={7} className="text-center py-10">
                    <div className="text-slate-700 font-semibold">No leads match your filters.</div>
                    <button
                      onClick={() => nav("/leads/import")}
                      data-testid="leads-empty-import-btn"
                      className="text-xs font-bold uppercase tracking-widest text-blue-600 hover:text-blue-700 mt-2"
                    >
                      Import a spreadsheet →
                    </button>
                  </td>
                </tr>
              )}
              {!loading && items.map((c) => (
                <tr
                  key={c.id}
                  className="cursor-pointer"
                  data-testid={`lead-row-${c.id}`}
                  onClick={() => nav(`/contacts/${c.id}`)}
                >
                  <td>
                    <div className="font-semibold text-slate-900">{c.client_name || "—"}</div>
                    <div className="text-xs text-slate-500">{c.possible_type || ""}</div>
                  </td>
                  <td className="text-slate-700">
                    <div>{c.property_street}</div>
                    <div className="text-xs text-slate-500">{c.property_city} {c.property_state} {c.property_zip}</div>
                  </td>
                  <td className="font-mono text-xs">{c.county} · {c.property_state}</td>
                  <td className="text-right font-mono font-semibold text-emerald-700">{money(c.surplus_amount)}</td>
                  <td className="font-mono text-xs text-slate-600">{c.case_number || "—"}</td>
                  <td>
                    <Badge variant="outline" className={`${STAGE_COLORS[c.stage] || ""} text-xs`}>
                      {stageLabel(c.stage)}
                    </Badge>
                  </td>
                  <td className="text-right font-mono">{c.qualification_score ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
